import { useState } from "react";
import { Button, ListGroup, Modal, Spinner } from "react-bootstrap";
import { mdiFormatListChecks, mdiDeleteOutline, mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQueryClient } from "react-query";

import type { ListarVariaveisResponse } from "../../model/listarVariaveisResponse";
import { deleteVariaveisDeletar, getGetVariaveisListarQueryKey } from "../../config/swagger";
import { useToast } from "../../context/ToastContext";
import ListCard from "../ListCard";
import { useNavigate } from "react-router-dom";

type VariaveisCardPropsContent = {
	data: ListarVariaveisResponse;
	skeleton?: never;
};

type VariaveisCardPropsSkeleton = {
	data?: never;
	skeleton: true;
};

type VariaveisCardProps = VariaveisCardPropsContent | VariaveisCardPropsSkeleton;

export default function VariaveisCard(props: VariaveisCardProps) {
	const navigate = useNavigate();
	const { handleToast } = useToast();
	const queryClient = useQueryClient();

	const [monitoresModal, setMonitoresModal] = useState(false);
	const [removeModal, setRemoveModal] = useState(false);
	const [removed, setRemoved] = useState(false);

	const deleteMutation = useMutation((id: number) => deleteVariaveisDeletar({ id }), {
		onSuccess: () => {
			queryClient.invalidateQueries(getGetVariaveisListarQueryKey());
			setRemoved(true);
			handleToast("Variável", "Variável removida com sucesso!", 5000);
			setRemoveModal(false);
		},
		onError: () => {
			handleToast("Variável", "Erro ao remover variável", 5000, "danger");
			setRemoveModal(false);
		},
	});

	function handleClick() {
		if (!props.skeleton && props.data && props.data?.tipo !== "system") {
			navigate("/variavelFormulario/" + props.data.id);
		}
	}

	function handleClickRemove(e: React.MouseEvent) {
		e.stopPropagation();
		setRemoveModal(true);
	}

	function handleClickMonitores(e: React.MouseEvent) {
		e.stopPropagation();
		setMonitoresModal(true);
	}

	async function handleRemove() {
		if (!props.skeleton && props.data?.id) {
			deleteMutation.mutate(props.data.id);
		}
	}

	function handleCloseModal() {
		if (!deleteMutation.isLoading) {
			setRemoveModal(false);
		}
	}

	if (removed) {
		return <></>;
	}

	let skeletonButton = "";
	if (props.skeleton) {
		skeletonButton = "skeleton skeleton-button";
	}

	return (
		<>
			<ListCard onClick={handleClick}>
				<ListCard.Content>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Nome</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.nome ?? "..."}</ListCard.Text>
					</ListCard.Item>
					{props.data?.tipo !== "system" && (
						<>
							<ListCard.Item>
								<ListCard.Title skeleton={props.skeleton}>Valor</ListCard.Title>
								<ListCard.Text skeleton={props.skeleton}>{props.data?.valor ?? "..."}</ListCard.Text>
							</ListCard.Item>
							<ListCard.Item>
								<ListCard.Title skeleton={props.skeleton}>Validade</ListCard.Title>
								<ListCard.Text skeleton={props.skeleton}>{props.data?.validade ?? "-"}</ListCard.Text>
							</ListCard.Item>
							<ListCard.Item>
								<ListCard.Title skeleton={props.skeleton}>Data Hora Atualização</ListCard.Title>
								<ListCard.Text skeleton={props.skeleton}>{props.data?.dataAtualizacao ?? "..."}</ListCard.Text>
							</ListCard.Item>
							<ListCard.Item>
								<ListCard.Title skeleton={props.skeleton}>Data Hora Validade</ListCard.Title>
								<ListCard.Text skeleton={props.skeleton}>{props.data?.dataValidade ?? "-"}</ListCard.Text>
							</ListCard.Item>
						</>
					)}
				</ListCard.Content>
				<ListCard.Action>
					<Button variant="light" className={`me-1 ${skeletonButton}`} title="Monitores Associados" onClick={handleClickMonitores}>
						<Icon path={mdiFormatListChecks} size={1} />
					</Button>
					{props.data?.tipo !== "system" && (
						<>
							<Button variant="light" className={`me-2 ${skeletonButton}`} onClick={handleClickRemove}>
								<Icon path={mdiDeleteOutline} size={1} className="text-danger" />
							</Button>
							<Icon path={mdiChevronRight} size={1} className={`text-muted m-2 ${skeletonButton}`} />
						</>
					)}
				</ListCard.Action>
			</ListCard>

			<Modal
				show={monitoresModal}
				onHide={() => {
					setMonitoresModal(false);
				}}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>
						Monitores Associados
						<div className="fs-6 fw-normal">
							<small>{props.data?.nome}</small>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{props.data?.monitores && props.data?.monitores.length > 0 ? (
						<ListGroup>
							{props.data?.monitores.map((monitorNome, index) => (
								<ListGroup.Item key={index}>{monitorNome}</ListGroup.Item>
							))}
						</ListGroup>
					) : (
						<p>Esta variável não está associada a nenhum monitor.</p>
					)}
				</Modal.Body>
			</Modal>

			<Modal show={removeModal} onHide={handleCloseModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Remover Variável</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">{!deleteMutation.isLoading ? "Deseja realmente remover esta variável?" : <Spinner />}</Modal.Body>
				{!deleteMutation.isLoading && (
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseModal}>
							Cancelar
						</Button>
						<Button variant="danger" onClick={handleRemove}>
							Remover
						</Button>
					</Modal.Footer>
				)}
			</Modal>
		</>
	);
}
